import * as React from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectContext } from "../../../../store/contextSlice";
import {
  selectFavoriteIds,
  selectExpandedSection,
  selectIsEditingFavorites,
  toggleFavorite,
  reorderFavorites,
  setExpandedSection,
  toggleSection,
  toggleEditingFavorites,
  MENU_EXPAND_EVENT,
} from "../../../../store/menuSlice";
import { getMenuItems } from "./menuConfig";
import { FavoritesSection } from "./FavoritesSection";
import { CollapsibleSection } from "./CollapsibleSection";
import { MetricsMenuItem } from "./MetricsMenuItem";
import "./newMenu.scss";

interface Props {
  menuCategories: { id: string; label: string; expandable }[];
  showFavorites?: boolean;
}
export const NewMenu: React.FC<Props> = ({ menuCategories, showFavorites }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { org, gvc } = useAppSelector(selectContext);
  const dashboard = useAppSelector((state) => state.uiData.dashboard);
  const favoriteIds = useAppSelector(selectFavoriteIds);
  const expandedSection = useAppSelector(selectExpandedSection);
  const isEditingFavorites = useAppSelector(selectIsEditingFavorites);

  const allItems = React.useMemo(() => getMenuItems(org, gvc), [org, gvc]);

  // Track the previous pathname to detect actual navigation
  const prevPathnameRef = React.useRef(pathname);

  // Auto-expand the category of the active page if it's not in favorites (only on navigation)
  React.useEffect(() => {
    // Only auto-expand when the pathname actually changes (navigation occurred)
    if (prevPathnameRef.current === pathname) {
      return;
    }
    prevPathnameRef.current = pathname;

    // Find the active menu item based on current pathname
    const activeItem = allItems.find((item) => {
      if (item.customIsActive) {
        return item.customIsActive(pathname);
      }
      return pathname.startsWith(item.url.split("?")[0]);
    });

    // If active item exists and is not in favorites, expand its category
    // But skip if the category is not expandable (like "health")
    if (activeItem && !favoriteIds.includes(activeItem.id)) {
      const category = menuCategories.find((c) => c.id === activeItem.category);
      if (category?.expandable) {
        dispatch(setExpandedSection(activeItem.category));
      }
    }
  }, [pathname, allItems, favoriteIds, dispatch, menuCategories]);

  // Listen for external requests to expand a section (from guides)
  React.useEffect(() => {
    const handleExpandEvent = (event: CustomEvent<string | null>) => {
      dispatch(setExpandedSection(event.detail));
    };

    window.addEventListener(MENU_EXPAND_EVENT, handleExpandEvent as EventListener);
    return () => window.removeEventListener(MENU_EXPAND_EVENT, handleExpandEvent as EventListener);
  }, [dispatch]);

  const favoriteItems = React.useMemo(() => {
    return favoriteIds
      .map((id) => allItems.find((item) => item.id === id))
      .filter((item): item is typeof allItems[number] => item !== undefined);
  }, [favoriteIds, allItems]);

  // Filter out metrics from collapsible items since we render it with MetricsMenuItem
  // const getFilteredItems = React.useCallback((items: typeof allItems) => {
  //   return items.filter((item) => item.id !== "metrics");
  // }, []);

  const handleToggleFavorite = React.useCallback(
    (id: string) => {
      dispatch(toggleFavorite(id));
    },
    [dispatch],
  );

  const handleReorderFavorites = React.useCallback(
    (oldIndex: number, newIndex: number) => {
      dispatch(reorderFavorites({ oldIndex, newIndex }));
    },
    [dispatch],
  );

  const handleToggleSection = React.useCallback(
    (sectionId: string) => {
      dispatch(toggleSection(sectionId));
    },
    [dispatch],
  );

  const handleToggleEditMode = React.useCallback(() => {
    dispatch(toggleEditingFavorites());
  }, [dispatch]);

  const shouldShowFavorites = dashboard === "console" || showFavorites;

  return (
    <div className="new-menu">
      {shouldShowFavorites ? (
        <>
          <FavoritesSection
            items={favoriteItems}
            favoriteIds={favoriteIds}
            onReorder={handleReorderFavorites}
            onUnpin={handleToggleFavorite}
            isEditMode={isEditingFavorites}
            onToggleEditMode={handleToggleEditMode}
          />
          {favoriteIds.length > 0 && <div className="menu-list-divider" />}
        </>
      ) : null}

      {menuCategories.map((category) => {
        const categoryItems = allItems.filter((item) => item.category === category.id);

        if (categoryItems.length === 0) return null;

        // Special handling for the Analyze section to include MetricsMenuItem
        const isAnalyze = category.id === "analyze";
        // const filteredItems = isAnalyze ? getFilteredItems(categoryItems) : categoryItems;

        return (
          <React.Fragment key={category.id}>
            <CollapsibleSection
              title={category.label}
              items={categoryItems}
              expandable={category.expandable}
              isExpanded={expandedSection === category.id}
              favoriteIds={favoriteIds}
              onToggle={() => handleToggleSection(category.id)}
              onToggleFavorite={handleToggleFavorite}
              // customContent={
              //   isAnalyze ? (
              //     <MetricsMenuItem
              //       isFavorited={favoriteIds.includes("metrics")}
              //       onToggleFavorite={handleToggleFavorite}
              //     />
              //   ) : undefined
              // }
            />
            <div className="menu-list-divider" />
          </React.Fragment>
        );
      })}
    </div>
  );
};
